import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MotifProgressBar } from '../../../progress-bar/progress-bar';
import { MotifChip } from '../../../chip/chip';

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  // const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * @typedef Props
 * @prop {React.ReactNode} [customButton]
 * @prop {string} [className]
 * @prop {boolean} [error]
 * @prop {boolean} [uploaded]
 * @prop {boolean} [disabled]
 * @prop {React.ReactNode} [fileIcon]
 * @prop {string} [fileName]
 * @prop {number} [fileSize]
 * @prop {boolean} [isIndeterminate]
 * @prop {() => void} [onRemove]
 * @prop {number} [percentUploaded]
 * @prop {boolean} [removable]
 * @prop {boolean} [showProgress]
 * @prop {{}} [closeButtonProps]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifFileUploaderItem = ({
  customButton = '',
  className = '',
  error = false,
  uploaded = false,
  disabled = false,
  fileIcon = null,
  fileName = '',
  fileSize = 0,
  isIndeterminate = false,
  onRemove = () => null,
  percentUploaded = 0,
  removable = false,
  showProgress = false,
  closeButtonProps = {},
  ...other
}) => {
  const classes = classNames({
    'motif-file-uploader-item': true,
    'motif-file-uploader-item-has-progress': showProgress,
    'motif-file-uploader-item-has-error': error,
    'motif-file-uploader-item-disabled': disabled,
    'motif-file-uploader-item-uploaded': uploaded,
    [className]: true
  });

  return (
    <MotifChip
      data-motif-file-uploader-item
      className={classes}
      removable={removable}
      onRemove={onRemove}
      disabled={disabled}
      variant={error ? 'error' : null}
      closeButtonProps={{ 'aria-label': `Click to remove file - ${fileName}`, ...closeButtonProps }}
      {...other}
    >
      <div className="motif-file-uploader-item-label-wrapper">
        <div className="motif-file-uploader-item-label">
          {fileIcon}
          <span className="motif-file-uploader-item-file-name">{fileName}</span>
          {!isIndeterminate && !error && fileSize && (
            <span className="motif-file-uploader-item-file-size">
              {`${
                !uploaded && !disabled
                  ? `${formatBytes(fileSize * (percentUploaded / 100))} of `
                  : ''
              }${formatBytes(fileSize)}`}
            </span>
          )}
        </div>
        {showProgress && !uploaded && (
          <MotifProgressBar isIndeterminate={isIndeterminate} value={percentUploaded} />
        )}
      </div>
      {customButton && (
        <div className="motif-file-uploader-item-custom-button-wrapper">{customButton}</div>
      )}
    </MotifChip>
  );
};

MotifFileUploaderItem.propTypes = {
  className: PropTypes.string,
  customButton: PropTypes.node,
  closeButtonProps: PropTypes.instanceOf(Object),
  error: PropTypes.bool,
  uploaded: PropTypes.bool,
  disabled: PropTypes.bool,
  isIndeterminate: PropTypes.bool,
  fileIcon: PropTypes.node,
  fileName: PropTypes.string,
  fileSize: PropTypes.number,
  onRemove: PropTypes.func,
  percentUploaded: PropTypes.number,
  removable: PropTypes.bool,
  showProgress: PropTypes.bool
};

export { MotifFileUploaderItem };
